import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  type TableColumnsType,
} from "antd";
import styles from "../MaterialFormFields.module.less";
import { PriceCell } from "../PriceCell";
import { useMaterialGroupOptions } from "@/hooks/services/useSelectOptionsQuery";
import { LowValuePurchaseRequisitionFormValue } from "../../../type";
import { useModalBehavior } from "@/hooks/behaviors";
import { NamePath } from "antd/es/form/interface";
import { useState } from "react";
import { useFormEditable } from "../../../hooks/useFormEditable";
import { unitOptions } from "@/constants";
import { useTranslation } from "react-i18next";

export function useMaterialCodeColumns() {
  const { t } = useTranslation("purchaseRequestManagement");
  const { t: tCommon } = useTranslation("common");
  const form = Form.useFormInstance<LowValuePurchaseRequisitionFormValue>();

  const { isMainFormDisabled, isPurchasingStaffFormFieldsDisabled } =
    useFormEditable();

  const materialGroupOptions = useMaterialGroupOptions();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const {
    open: pricePopupOpen,
    openModal: openPricePopup,
    closeModal: closePricePopup,
  } = useModalBehavior();

  const itemList = Form.useWatch(["itemList"], form) || [];

  const handlePriceSave = (values: {
    price: number;
    priceUnit: string;
    priceHkd: number;
  }) => {
    if (editingIndex !== null) {
      form.setFieldValue(["itemList", editingIndex, "price"], values.price);
      form.setFieldValue(
        ["itemList", editingIndex, "priceUnit"],
        values.priceUnit,
      );
      form.setFieldValue(
        ["itemList", editingIndex, "priceHkd"],
        values.priceHkd,
      );
      // 每次 priceHkd 变化时都重新计算 applyAmtHkd
      const priceHkd = values.priceHkd;
      const applyQty = form.getFieldValue([
        "itemList",
        editingIndex,
        "applyQty",
      ]);
      if (applyQty && priceHkd) {
        form.setFieldValue(
          ["itemList", editingIndex, "applyAmtHkd"],
          (priceHkd * applyQty).toFixed(2),
        );
      }
    }
    setEditingIndex(null);
  };

  const handleQtyChange = (index: number, applyQty: number) => {
    // 每次 applyQty 变化时都重新计算 applyAmtHkd
    const priceHkd = form.getFieldValue(["itemList", index, "priceHkd"]);
    if (priceHkd) {
      form.setFieldValue(
        ["itemList", index, "applyAmtHkd"],
        (priceHkd * applyQty).toFixed(2),
      );
    }
  };

  const getFieldValue = (name: number, field: NamePath) => {
    return form?.getFieldValue(["itemList", name, field]);
  };

  const columns: TableColumnsType = [
    {
      title: t("分类"),
      dataIndex: "materialType",
      width: 100,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "materialType")}
        />
      ),
    },
    {
      title: t("物料编号"),
      dataIndex: "materialCode",
      width: 120,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "materialCode")}
        />
      ),
    },

    {
      title: <span className={styles.required}>{t("物料组")}</span>,
      dataIndex: "materialGroup",
      width: 200,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "materialGroup"]}
          rules={[
            {
              required: true,
              message: t("请选择物料组"),
            },
          ]}
          noStyle
        >
          <Select
            style={{ width: "100%" }}
            placeholder={t("请选择")}
            options={materialGroupOptions}
            showSearch
            optionFilterProp="label"
            disabled={isPurchasingStaffFormFieldsDisabled}
          />
        </Form.Item>
      ),
    },
    {
      title: t("物料名称"),
      dataIndex: "materialName",
      width: 150,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "materialName")}
        />
      ),
    },
    {
      title: <span className={styles.required}>{t("申请数量")}</span>,
      dataIndex: "applyQty",
      width: 120,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "applyQty"]}
          rules={[
            {
              required: true,
              message: t("请输入申请数量"),
            },
          ]}
          noStyle
        >
          <InputNumber
            min={1}
            precision={0}
            placeholder={t("请输入")}
            onChange={(value) => handleQtyChange(field.name, value as number)}
            disabled={isPurchasingStaffFormFieldsDisabled}
          />
        </Form.Item>
      ),
    },
    {
      title: t("单位"),
      dataIndex: "unit",
      width: 120,
      align: "center",
      render: (_, field) => (
        <Form.Item name={[field.name, "unit"]} noStyle>
          <Select
            style={{ width: "100%" }}
            placeholder={t("请选择")}
            options={unitOptions}
            showSearch
            optionFilterProp="label"
            disabled={isPurchasingStaffFormFieldsDisabled}
          />
        </Form.Item>
      ),
    },
    {
      title: t("每组单位"),
      dataIndex: "setPerUnit",
      width: 100,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "setPerUnit")}
        />
      ),
    },
    {
      title: <span className={styles.required}>{t("要求到货日期")}</span>,
      dataIndex: "deliveryDate",
      width: 200,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "deliveryDate"]}
          rules={[
            {
              required: true,
              message: t("请选择日期"),
            },
          ]}
          noStyle
        >
          <DatePicker placeholder={t("请选择")} style={{ width: "100%" }} />
        </Form.Item>
      ),
    },
    {
      title: t("单价HKD"),
      dataIndex: "priceHkd",
      width: 120,
      align: "center",
      render: (_, field) => (
        <PriceCell
          disabled={isPurchasingStaffFormFieldsDisabled}
          fieldName={field.name}
          editingIndex={editingIndex}
          pricePopupOpen={pricePopupOpen}
          onOpenEdit={(index) => {
            setEditingIndex(index);
            openPricePopup();
          }}
          onClose={() => {
            closePricePopup();
            setEditingIndex(null);
          }}
          onSave={handlePriceSave}
        />
      ),
    },
    {
      title: t("LastPOUnitPrice"),
      dataIndex: "lastPoUnitPrice",
      width: 130,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "lastPoUnitPrice")}
        />
      ),
    },
    {
      title: t("新旧价格变动"),
      dataIndex: "newPriceChange",
      width: 120,
      align: "center",
      render: (_, field) => {
        const item = itemList[field.name];
        const priceHkd = Number(item?.priceHkd) || 0;
        const lastPoUnitPrice = Number(item?.lastPoUnitPrice) || 0;

        let value = "";
        if (lastPoUnitPrice === 0) {
          value = "100%";
        } else {
          const change = ((priceHkd - lastPoUnitPrice) / lastPoUnitPrice) * 100;
          value = `${change.toFixed(2)}%`;
        }

        return <Input value={value} placeholder={t("请输入")} disabled />;
      },
    },
    {
      title: t("年采购均价"),
      dataIndex: "yearBidPrice",
      width: 120,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "yearBidPrice")}
        />
      ),
    },
    {
      title: t("最后PO供应商编号"),
      dataIndex: "lastPoSupplierCode",
      width: 130,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "lastPoSupplierCode")}
        />
      ),
    },
    {
      title: t("最后PO供应商名称"),
      dataIndex: "lastPoSupplierName",
      width: 150,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "lastPoSupplierName")}
        />
      ),
    },
    {
      title: t("最后PO币种"),
      dataIndex: "lastPoPriceUnit",
      width: 100,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "lastPoPriceUnit")}
        />
      ),
    },
    {
      title: t("上次PO价格数量单位"),
      dataIndex: "lastPoUnit",
      width: 100,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "lastPoUnit")}
        />
      ),
    },
    {
      title: t("最后PO数量"),
      dataIndex: "lastPoQty",
      width: 120,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "lastPoQty")}
        />
      ),
    },
    {
      title: t("现有库存"),
      dataIndex: "closingStock",
      width: 100,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "closingStock")}
        />
      ),
    },

    {
      title: t("申请总金额HKD"),
      dataIndex: "applyAmtHkd",
      width: 130,
      align: "center",
      render: (_, field) => (
        <Form.Item name={[field.name, "applyAmtHkd"]} noStyle>
          <InputNumber min={0} precision={2} placeholder={t("自动计算")} />
        </Form.Item>
      ),
    },

    {
      title: <span className={styles.required}>{t("采购说明")}</span>,
      dataIndex: "applyRemarks",
      width: 180,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "applyRemarks"]}
          rules={[
            {
              required: true,
              message: t("请输入采购说明"),
            },
          ]}
          noStyle
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },

    {
      title: <span className={styles.required}>{t("采购原因")}</span>,
      dataIndex: "applyReason",
      width: 180,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "applyReason"]}
          rules={[
            {
              required: true,
              message: t("请输入采购原因"),
            },
          ]}
          noStyle
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },

    {
      title: t("安全库存"),
      dataIndex: "safetyStock",
      width: 100,
      align: "center",
      render: (_, field) => (
        <Input
          placeholder={t("请输入")}
          disabled
          value={getFieldValue(field.name, "safetyStock")}
        />
      ),
    },
    {
      title: t("优先采购"),
      dataIndex: "highestPriority",
      width: 160,
      align: "center",
      render: (_, field) => (
        <Form.Item name={[field.name, "highestPriority"]} noStyle>
          <Radio.Group>
            <Radio value={1}>{tCommon("是")}</Radio>
            <Radio value={0}>{tCommon("否")}</Radio>
          </Radio.Group>
        </Form.Item>
      ),
    },
    {
      title: t("上传附件"),
      dataIndex: "attachments",
      width: 500,
      render: (_, field) => (
        <div style={{ marginBottom: -24 }}>
          <MaterialFileFormFields
            parentFieldName="itemList"
            fieldName={[field.name, "attachments"]}
            label=""
            required={false}
            hiddenRemove={isMainFormDisabled}
          />
        </div>
      ),
    },
  ];
  return columns;
}
